import React, { useState, useEffect } from 'react';
import soundManager from '../../utils/soundEffects';
import Sparkle from '../Sparkle';

const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '', tags: '' });

  useEffect(() => {
    // Load posts from localStorage (persisted across sessions)
    const stored = localStorage.getItem('blogPosts');
    if (stored) {
      setPosts(JSON.parse(stored));
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    soundManager.playSuccess();
    
    const newPost = {
      id: Date.now(),
      title: formData.title,
      content: formData.content,
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      timestamp: new Date().toISOString()
    };
    
    const updatedPosts = [newPost, ...posts];
    setPosts(updatedPosts);
    // Save to localStorage - persists even after browser refresh/restart
    localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
    
    setFormData({ title: '', content: '', tags: '' });
    setShowForm(false);
  };

  const handleDelete = (postId) => {
    soundManager.playClick();
    const updatedPosts = posts.filter(post => post.id !== postId);
    setPosts(updatedPosts);
    localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
  };

  return (
    <div className="window mb-6">
      <div className="window-header">
        <span>📝 My Writings</span>
      </div>
      <div className="window-body">
        <div className="text-center mb-4">
          <h2 className="text-xl font-bold pixel-text mb-2" style={{ color: '#ff1493' }}>
            Thoughts & Adventures 💖
          </h2>
          <p className="text-xs text-gray-600">Code, learn, share, repeat!</p>
        </div>

        {/* Create Post Button */}
        <div className="mb-4 text-center">
          <Sparkle onInteract={() => {
            soundManager.playClick();
            setShowForm(!showForm);
          }}>
            <button className="retro-btn text-xs">
              {showForm ? '❌ Cancel' : '➕ Create Post'}
            </button>
          </Sparkle>
        </div>

        {/* Create Post Form */}
        {showForm && (
          <div className="bg-pink-50 p-4 border-2 border-pink-400 mb-4">
            <h3 className="font-bold text-sm mb-3 pixel-text" style={{ color: '#ff1493' }}>
              ✨ New Post
            </h3>
            <form onSubmit={handleSubmit} className="space-y-2">
              <div>
                <label className="block text-xs font-bold mb-1">Title:</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  onClick={() => soundManager.playClick()}
                  required
                  className="w-full px-2 py-1 border-2 border-gray-400 text-xs focus:outline-none focus:border-pink-400"
                  placeholder="Post title..."
                />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">Content:</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  onClick={() => soundManager.playClick()}
                  required
                  rows="4"
                  className="w-full px-2 py-1 border-2 border-gray-400 text-xs focus:outline-none focus:border-pink-400 resize-none"
                  placeholder="Write your thoughts..."
                ></textarea>
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">Tags (comma-separated):</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  onClick={() => soundManager.playClick()}
                  className="w-full px-2 py-1 border-2 border-gray-400 text-xs focus:outline-none focus:border-pink-400"
                  placeholder="AI, Coding, Projects..."
                />
              </div>
              <Sparkle onInteract={() => soundManager.playClick()}>
                <button type="submit" className="retro-btn text-xs w-full">
                  💾 Publish Post
                </button>
              </Sparkle>
            </form>
          </div>
        )}

        {/* Blog Posts */}
        {posts.length === 0 ? (
          <div className="text-center p-6 bg-purple-50 border-2 border-dashed border-purple-300">
            <p className="text-sm font-bold mb-1">🚧 No posts yet! 🚧</p>
            <p className="text-xs text-gray-600">Click "Create Post" to write your first post!</p>
            <p className="text-xs text-gray-500 mt-2">✨ Posts are saved permanently in your browser!</p>
          </div>
        ) : (
          <div>
            <div className="mb-3 text-center">
              <p className="text-xs text-gray-600">
                💾 You have {posts.length} post{posts.length !== 1 ? 's' : ''} (saved locally)
              </p>
            </div>
            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="bg-gradient-to-br from-pink-50 to-purple-50 p-3 border-2 border-pink-300"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-sm font-bold pixel-text flex-1" style={{ color: '#ff1493' }}>
                      {post.title}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-purple-200 px-2 py-0.5 rounded border border-purple-400">
                        {post.date}
                      </span>
                      <Sparkle onInteract={() => handleDelete(post.id)}>
                        <button
                          className="text-red-600 hover:text-red-800 font-bold text-sm"
                          title="Delete post"
                        >
                          🗑️
                        </button>
                      </Sparkle>
                    </div>
                  </div>

                  <p className="text-xs mb-2 text-gray-700">
                    {post.content}
                  </p>

                  {post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {post.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-pink-200 text-xs font-semibold border border-pink-400 rounded"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogSection;
